import { Box } from "@mui/system";
import {
  Button,
  Card,
  Fade,
  Grid,
  IconButton,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  courseListSecondaryIcon,
  filterLightIcon,
  filterPrimaryIcon,
} from "assets/images";
import projectTheme from "app.theme";
import React, { useEffect, useState } from "react";
import {
  BLENDED_FACE_TO_FACE_ONLINE,
  DEFAULT_PAGE_NO,
  DEFAULT_PAGE_SIZE,
  VIEW_TOOLTIP_TITLE,
} from "utils/constants/constant";
import {
  ICurrentCourseFilter,
  IEducatorHomeListMeta,
  IEnhancedTableProps,
  IPastCourse,
  IPastCoursePayloadValues,
  IPastCoursesDetails,
} from "utils/interfaces/educator-home";
import { Order, OrderByOptions } from "utils/enums/sorting";
import {
  getPagePayloadValuesWithId,
  hideLoaderForBatchApiCall,
  showLoaderForBatchApiCall,
} from "utils/helper";
import { PastCourseListSuccessPayload } from "store/educatorHome/types";
import { useHistory } from "react-router-dom";
import NoRecords from "components/NoRecords";
import { AppRoutings } from "utils/enums/app-routings";
import CustomPagination from "components/customPagination";
import PastCoursesFilter from "../PastCoursesFilter";

const EnhancedTableHead = (props: IEnhancedTableProps) => {
  const { order, orderBy, onRequestSort, fieldName, keyName, sxStyle } = props;
  const createSortHandler =
    (newOrderBy: keyof IPastCoursesDetails) =>
    (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, newOrderBy);
    };

  return (
    <TableCell
      key={keyName}
      align="left"
      onClick={createSortHandler(keyName)}
      sortDirection={orderBy === keyName ? order : false}
      sx={sxStyle}
      className="term-sorting"
    >
      {fieldName}
      <Box component="span" className="sorting-icon" />
    </TableCell>
  );
};

const PastCoursesList: React.FC<any> = (props) => {
  const history = useHistory();
  const [page, setPage] = useState(DEFAULT_PAGE_SIZE);
  const [pageNo, setPageNo] = useState<number>(DEFAULT_PAGE_NO);
  const [order, setOrder] = useState<Order>(OrderByOptions.Asc);
  const [orderBy, setOrderBy] = useState<keyof IPastCoursesDetails>("course");
  const [pastCoursesList, setPastCoursesList] = useState<IPastCourse[]>();
  const [pastCoursesListMeta, setPastCoursesListMeta] =
    useState<IEducatorHomeListMeta>();
  const { setFilterBox, filterBox } = props;
  const handleChange = (event: SelectChangeEvent) => {
    setPage(event.target.value as string);
    setPageNo(1);
  };
  const [filteredData, setFilteredData] = useState<ICurrentCourseFilter>();
  const onPageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPageNo(value);
  };

  const handleRequestSort = React.useCallback(
    (
      event: React.MouseEvent<unknown>,
      newOrderBy: keyof IPastCoursesDetails
    ) => {
      const isAsc = orderBy === newOrderBy && order === OrderByOptions.Asc;
      const toggledOrder = isAsc ? OrderByOptions.Desc : OrderByOptions.Asc;
      setOrder(toggledOrder);
      setOrderBy(newOrderBy);
    },
    [order, orderBy]
  );

  const onPastCoursesListSuccess = (response: PastCourseListSuccessPayload) => {
    setPastCoursesList(response.pastCourseList);
    setPastCoursesListMeta(response.pastCourseListPagination);
    hideLoaderForBatchApiCall();
  };

  const getPastCoursesList = (filterData?: ICurrentCourseFilter) => {
    const { pastCourseListRequest } = props;

    if (pastCourseListRequest) {
      showLoaderForBatchApiCall();
      const values: IPastCoursePayloadValues = getPagePayloadValuesWithId(
        props?.userId,
        orderBy,
        order,
        pageNo,
        page
      );
      if (filterData?.course) {
        values.course = filterData?.course;
      }
      if (filterData?.termId) {
        values.termId = filterData?.termId;
      }

      if (filterData) {
        setFilteredData(filterData);
      } else {
        setFilteredData(undefined);
      }
      const payload = {
        values,
        callback: onPastCoursesListSuccess,
      };

      pastCourseListRequest(payload);
    }
  };

  useEffect(() => {
    if (filteredData) {
      getPastCoursesList(filteredData);
    } else {
      getPastCoursesList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageNo, order, orderBy]);

  const handleFilterApplyClick = (formData: ICurrentCourseFilter) => {
    setFilteredData(formData);
    if (pageNo === DEFAULT_PAGE_NO) {
      getPastCoursesList(formData);
    } else {
      setPageNo(1);
    }
    setFilterBox(false);
    document.body.classList.toggle("filter-open");
  };

  return (
    <>
      <Grid item xs={12}>
        <Card>
          <Box className="table-card-header">
            <Box sx={{ justifyContent: "space-between", width: "100%" }}>
              <Typography variant="h3">My Past Courses</Typography>
              <Button
                variant="outlined"
                className="btn-filter"
                onClick={() => {
                  setFilterBox(true);
                  document.body.classList.toggle("filter-open");
                }}
              >
                <img src={filterPrimaryIcon} alt="filter" className="default" />
                <img src={filterLightIcon} alt="filter" className="active" />
                Filter
              </Button>
            </Box>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <EnhancedTableHead
                    order={order}
                    orderBy={orderBy}
                    onRequestSort={(e) => handleRequestSort(e, "course")}
                    fieldName="Session Name"
                    keyName="course"
                    sxStyle={{
                      width: "302px",
                      minWidth: "302px",
                      [projectTheme.breakpoints.down("lg")]: {
                        width: "210px",
                        minWidth: "210px",
                      },
                    }}
                  />
                  <EnhancedTableHead
                    order={order}
                    orderBy={orderBy}
                    onRequestSort={(e) => handleRequestSort(e, "term")}
                    fieldName="Term"
                    keyName="term"
                    sxStyle={{
                      width: "110px",
                      minWidth: "110px",
                      [projectTheme.breakpoints.down("lg")]: {
                        width: "94px",
                        minWidth: "94px",
                      },
                    }}
                  />
                  <EnhancedTableHead
                    order={order}
                    orderBy={orderBy}
                    onRequestSort={(e) => handleRequestSort(e, "location")}
                    fieldName="Location"
                    keyName="location"
                    sxStyle={{
                      width: "382px",
                      minWidth: "382px",
                    }}
                  />
                  <EnhancedTableHead
                    order={order}
                    orderBy={orderBy}
                    onRequestSort={(e) => handleRequestSort(e, "startDate")}
                    fieldName="Date & Time of First Session"
                    keyName="startDate"
                    sxStyle={{
                      width: "255px",
                      minWidth: "255px",
                      [projectTheme.breakpoints.down("lg")]: {
                        width: "254px",
                        minWidth: "254px",
                      },
                    }}
                  />
                  <TableCell
                    align="center"
                    sx={{
                      width: "98px",
                      [projectTheme.breakpoints.down("sm")]: {
                        width: "90px",
                      },
                    }}
                  >
                    Details
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pastCoursesList?.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell component="th" scope="row">
                      <Typography
                        variant="body2"
                        className="sco-list-word-wrap"
                      >
                        {row.courseName}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.term}</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        className="location sco-list-word-wrap"
                      >
                        {row.location}
                        {row.isBlended && BLENDED_FACE_TO_FACE_ONLINE}
                      </Typography>
                    </TableCell>
                    <TableCell>{`${row.startDate} ${row.startTime} to ${row.endTime}`}</TableCell>
                    <TableCell align="center">
                      <div className="table-actions">
                        <Tooltip
                          TransitionComponent={Fade}
                          TransitionProps={{ timeout: 600 }}
                          title={VIEW_TOOLTIP_TITLE}
                          arrow
                        >
                          <IconButton
                            onClick={() =>
                              history.push({
                                pathname: AppRoutings.EducatorsHomeDetails,
                                state: { scoId: row.id },
                              })
                            }
                            disabled={!props?.pagePermission?.edit}
                            className={
                              !props?.pagePermission?.edit
                                ? "disabled-icon"
                                : ""
                            }
                          >
                            <img src={courseListSecondaryIcon} alt="list" />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {pastCoursesList && pastCoursesList.length > 0 ? (
            <CustomPagination
              handleChange={handleChange}
              page={page}
              pageNo={pageNo}
              onPageChange={onPageChange}
              listMeta={pastCoursesListMeta}
            />
          ) : (
            <NoRecords />
          )}
        </Card>
      </Grid>
      {filterBox && (
        <PastCoursesFilter
          filterBox={filterBox}
          setFilterBox={setFilterBox}
          handleApplyClick={handleFilterApplyClick}
          filteredData={filteredData}
          pageNo={pageNo}
          setPageNo={setPageNo}
          setFilteredData={setFilteredData}
          getCurrentCoursesList={getPastCoursesList}
        />
      )}
    </>
  );
};

export default PastCoursesList;
